import useClientMetadata from "hooks/useClientMetadata"
import { useEffect, useMemo, useState } from "react"
import { groupBy, stripHTML } from "lib/utils"
import { useNotifications } from "lib/NotificationsProvider"
import { GetRemoteIdFn, useOrbit } from "providers/OrbitProvider"
import type {
  ClientCampaignRecord,
  ClientRecord,
  ContractRecord,
  DegreeProgramRecord,
  VendorRecord,
} from 'lib/DataModel'
import type { LeadCustomField, LeadTableLookupFilterGroup, LookupMap, StringOptionMap } from './table/leadTableTypes'
import { InitializedRecord } from "@orbit/records"

type MetadataState = {
  loading: boolean
  clientIdToName: LookupMap
  clientCampaignIdToName: LookupMap
  vendorIdToName: LookupMap
  degreeProgramIdToName: LookupMap
  campaignsByClient: LeadTableLookupFilterGroup[]
  degreeProgramsByClient: LeadTableLookupFilterGroup[]
}

const INITIAL_METADATA_STATE: MetadataState = {
  loading: true,
  clientIdToName: {},
  clientCampaignIdToName: {},
  vendorIdToName: {},
  degreeProgramIdToName: {},
  campaignsByClient: [],
  degreeProgramsByClient: [],
}

const UNKNOWN_CLIENT_ID = 'unknown'

function getRelatedId(record: InitializedRecord, relationship: string, store, getRemoteId: GetRemoteIdFn) {
  const relatedRecord = store.cache.query(q => q.findRelatedRecord(record, relationship))
  if(relatedRecord) {
    return getRemoteId(relatedRecord)
  }
  return undefined
}

function mapToName(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  records: Array<{ id: string; attributes: Record<string, any> }>,
  getRemoteId: (record: { id: string }) => string | null | undefined
): LookupMap {
  return records.reduce<LookupMap>((hash, record) => {
    const remoteId = getRemoteId(record)

    if (!remoteId) {
      return hash
    }

    hash[String(remoteId)] = stripHTML(
      record.attributes.internalName ||
        record.attributes.name ||
        record.attributes.description ||
        ''
    )

    return hash
  }, {})
}

export function useLeadTableMeta({
  accessibleClientIds,
}: {
  accessibleClientIds: string[]
}) {
  const { store, remote, getRemoteId } = useOrbit()
  const { addNotification } = useNotifications()

  const [metadataState, setMetadataState] = useState<MetadataState>(INITIAL_METADATA_STATE)

  useEffect(() => {
    let cancelled = false

    async function loadMetadata() {
      setMetadataState((prev) => ({
        ...prev,
        loading: true,
      }))

      try {
        const [clients, vendors, degreePrograms, contracts, clientCampaigns] = await Promise.all([
          remote.query<ClientRecord[]>((q) => q.findRecords('client')),
          remote.query<VendorRecord[]>((q) => q.findRecords('vendor')),
          remote.query<DegreeProgramRecord[]>(
            (q) => q.findRecords('degreeProgram'),
            {
              sources: {
                remote: {
                  settings: {
                    params: {
                      fields: {
                        'degree-program': ['name', 'client'].join(','),
                      },
                    },
                  },
                },
              },
            }
          ),
          remote.query<ContractRecord[]>(
            (q) => q.findRecords('contract'),
            {
              sources: {
                remote: {
                  settings: {
                    params: {
                      fields: {
                        contract: ['client'].join(','),
                      },
                    },
                  },
                },
              },
            }
          ),
          remote.query<ClientCampaignRecord[]>(
            (q) => q.findRecords('clientCampaign'),
            {
              sources: {
                remote: {
                  settings: {
                    params: {
                      fields: {
                        'client-campaign': ['name', 'contract'].join(','),
                      },
                    },
                  },
                },
              },
            }
          ),
        ])

        if (cancelled) {
          return
        }

        const clientIdToName = mapToName(clients, getRemoteId)
        const clientCampaignIdToName = mapToName(clientCampaigns, getRemoteId)

        const contractIdToClientId = Object.fromEntries(
          contracts.flatMap((contract) => {
            const clientId = getRelatedId(contract, 'client', store, getRemoteId)

            if (!clientId) {
              return []
            }

            return [
              [contract.id, clientId],
              [getRemoteId(contract), clientId],
            ]
          })
        )

        const getClientCampaignClientId = (
          clientCampaign: ClientCampaignRecord
        ) => {
          const contractId = getRelatedId(clientCampaign, 'contract', store, getRemoteId)

          if (!contractId) {
            return UNKNOWN_CLIENT_ID
          }

          return contractIdToClientId[contractId] ?? UNKNOWN_CLIENT_ID
        }

        const clientCampaignsByClient = groupBy(
          clientCampaigns,
          getClientCampaignClientId,
          {
            transform: (clientCampaign: ClientCampaignRecord) => getRemoteId(clientCampaign),
          }
        ) as Record<string, string[]>

        const clientCampaignGroups = Object.entries(clientCampaignsByClient)
          .map(([clientId, clientCampaignIds]) => ({
            title:
              clientId === UNKNOWN_CLIENT_ID
                ? 'Unknown Client'
                : clientIdToName[clientId] ?? `Client ${clientId}`,
            keys: clientCampaignIds,
          }))
          .sort((a, b) => a.title.localeCompare(b.title))

        const degreeProgramsByClient = groupBy(
          degreePrograms,
          'relationships.client.data.id',
          {
            transform: (degreeProgram: DegreeProgramRecord) => degreeProgram.id,
          }
        ) as Record<string, string[]>

        const degreeProgramGroups = Object.entries(degreeProgramsByClient)
          .map(([clientId, degreeProgramIds]) => ({
            title: clientIdToName[clientId] ?? `Client ${clientId}`,
            keys: degreeProgramIds,
          }))
          .sort((a, b) => a.title.localeCompare(b.title))

        setMetadataState({
          loading: false,
          clientIdToName,
          clientCampaignIdToName,
          vendorIdToName: mapToName(vendors, getRemoteId),
          degreeProgramIdToName: mapToName(degreePrograms, getRemoteId),
          degreeProgramsByClient: degreeProgramGroups,
          campaignsByClient: clientCampaignGroups,
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        if (cancelled) {
          return
        }

        addNotification({ variant: 'alert', message: 'Error loading leads metadata' })

        setMetadataState((prev) => ({
          ...prev,
          loading: false,
        }))
      }
    }

    loadMetadata()

    return () => {
      cancelled = true
    }
  }, [addNotification, getRemoteId, remote, store])

  const {
    statuses,
    dispositions,
    clientCampaignCustomAttributes,
    leadCustomFields,
  } = useClientMetadata(accessibleClientIds)

  const normalizedStatuses = useMemo<StringOptionMap>(() => {
    return statuses ?? {}
  }, [statuses])

  const normalizedDispositions = useMemo<StringOptionMap>(() => {
    return dispositions ?? {}
  }, [dispositions])

  const normalizedClientCampaignCustomAttributes = useMemo<string[]>(() => {
    return clientCampaignCustomAttributes ?? []
  }, [clientCampaignCustomAttributes])

  const normalizedLeadCustomFields = useMemo<LeadCustomField[]>(() => {
    return leadCustomFields ?? []
  }, [leadCustomFields])

  return {
    loadingMetadata: metadataState.loading,
    clientIdToName: metadataState.clientIdToName,
    clientCampaignIdToName: metadataState.clientCampaignIdToName,
    vendorIdToName: metadataState.vendorIdToName,
    degreeProgramIdToName: metadataState.degreeProgramIdToName,
    degreeProgramsByClient: metadataState.degreeProgramsByClient,
    campaignsByClient: metadataState.campaignsByClient,
    statuses: normalizedStatuses,
    dispositions: normalizedDispositions,
    clientCampaignCustomAttributes: normalizedClientCampaignCustomAttributes,
    leadCustomFields: normalizedLeadCustomFields,
  }
}
